/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import DataTable from "layouts/attendance/Tables/DataTable";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import {
  Chip,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Card,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Event,
  AccessTime,
  PauseCircle,
  Info,
  WbSunny,
  PlayCircle,
  Coffee,
  MoreTime,
  CalendarMonth,
  Refresh,
} from "@mui/icons-material";
import { getAttendenceStatus } from "api/attendance";
import FlagLoader from "layouts/flagloader";

// 🔹 Color configuration based on your API status
const STATUS_COLORS = {
  present: {
    color: "#10B981",
    bgColor: "#e8f5e9",
    icon: <CheckCircle sx={{ color: "#10B981", fontSize: 16, mr: 1 }} />,
    label: "Present",
  },
  full: {
    color: "#10B981",
    bgColor: "#e8f5e9",
    icon: <CheckCircle sx={{ color: "#10B981", fontSize: 16, mr: 1 }} />,
    label: "Full",
  },
  absent: {
    color: "#EF4444",
    bgColor: "#ffebee",
    icon: <Cancel sx={{ color: "#EF4444", fontSize: 16, mr: 1 }} />,
    label: "Absent",
  },
  weekend_off: {
    color: "#F97316",
    bgColor: "#ffedd5",
    icon: <WbSunny sx={{ color: "#F97316", fontSize: 16, mr: 1 }} />,
    label: "Weekend Off",
  },
  holiday: {
    color: "#F97316",
    bgColor: "#ffedd5",
    icon: <WbSunny sx={{ color: "#F97316", fontSize: 16, mr: 1 }} />,
    label: "Holiday",
  },
  running: {
    color: "#3B82F6",
    bgColor: "#e0f2fe",
    icon: <PlayCircle sx={{ color: "#3B82F6", fontSize: 16, mr: 1 }} />,
    label: "Running",
  },
  already_logged_in: {
    color: "#3B82F6",
    bgColor: "#e0f2fe",
    icon: <PlayCircle sx={{ color: "#3B82F6", fontSize: 16, mr: 1 }} />,
    label: "Logged In",
  },
  half: {
    color: "#525253",
    bgColor: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",

    icon: (
      <Box
        sx={{
          width: 13,
          height: 13,
          borderRadius: "50%",
          background: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
          mr: 1,
          border: "1px solid #E5E7EB",
        }}
      />
    ),

    label: "Half Day",
  },

  default: {
    color: "#6B7280",
    bgColor: "#f3f4f6",
    icon: <Info sx={{ color: "#6B7280", fontSize: 16, mr: 1 }} />,
    label: "Unknown",
  },
};

// 🔹 Get status configuration
const getStatusConfig = (status, attendance_status) => {
  const statusKey = status;

  // if (attendance_status === "running") {
  //   return STATUS_COLORS.running;
  // }

  return STATUS_COLORS[statusKey] || STATUS_COLORS.default;
};

// 🔹 Status cell component
const StatusCell = ({ status, status_reason, attendance_status }) => {
  const config = getStatusConfig(status, status_reason, attendance_status);

  let displayLabel = config.label;
  if (status === "full") {
    displayLabel = "Full Day";
  }

  return (
    <SoftBox display="flex" alignItems="center">
      {config.icon}
      <Chip
        label={displayLabel}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          minWidth: 80,
          "& .MuiChip-label": {
            px: 1.5,
          },
        }}
      />
    </SoftBox>
  );
};

StatusCell.propTypes = {
  status: PropTypes.string,
  status_reason: PropTypes.string,
  attendance_status: PropTypes.string,
};

// 🔹 Format time from 24-hour to 12-hour
const formatTime = (timeStr) => {
  if (!timeStr) return "-";
  try {
    const parts = timeStr.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parts[1] || "00";
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch (error) {
    return timeStr;
  }
};

// 🔹 Format time for display (remove seconds)
const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return "-";
  try {
    const parts = timeStr.split(":");
    return `${parts[0]}:${parts[1]}`;
  } catch (error) {
    return timeStr;
  }
};

// 🔹 Calculate hours from minutes
const calculateHours = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === 0) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};

// 🔹 Break count display
const BreaksCell = ({ minutes, isRunning }) => {
  if (!minutes || minutes === 0) return "-";

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Chip
        icon={<Coffee sx={{ fontSize: 14 }} />}
        label={`${minutes}`}
        size="small"
        sx={{
          backgroundColor: isRunning ? "#fef3c7" : "#e0f2fe",
          color: isRunning ? "#d97706" : "#0ea5e9",
          fontSize: "0.7rem",
          height: 22,
          fontWeight: isRunning ? "bold" : 500,
        }}
      />
      {isRunning && (
        <Chip
          label="Active"
          size="small"
          sx={{
            backgroundColor: "#fef3c7",
            color: "#d97706",
            fontSize: "0.6rem",
            height: 18,
            fontWeight: "bold",
          }}
        />
      )}
    </Stack>
  );
};

BreaksCell.propTypes = {
  minutes: PropTypes.number,
  isRunning: PropTypes.bool,
};

// 🔹 Shift Time Cell Component
const ShiftTimeCell = ({ shift_start, shift_end }) => {
  if (!shift_start || !shift_end) return "-";

  return (
    <Tooltip
      title={`Shift: ${formatTimeDisplay(shift_start)} - ${formatTimeDisplay(shift_end)}`}
      arrow
    >
      <SoftBox display="flex" alignItems="center">
        <AccessTime sx={{ fontSize: 16, color: "#6b7280", mr: 0.5 }} />
        {formatTimeDisplay(shift_start)} - {formatTimeDisplay(shift_end)}
      </SoftBox>
    </Tooltip>
  );
};

ShiftTimeCell.propTypes = {
  shift_start: PropTypes.string,
  shift_end: PropTypes.string,
};

// 🔹 Format date to YYYY-MM-DD for API
const formatDateToAPI = (date) => {
  return date.format("YYYY-MM-DD");
};

// 🔹 Main AttendanceTable Component
const AttendanceTable = ({
  selectedMonth: externalMonth,
  selectedYear: externalYear,
  onMonthChange,
  onYearChange,
  onDayClick,
  selectedUser = null,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(
    externalMonth !== undefined ? externalMonth : new Date().getMonth()
  );
  const [selectedYear, setSelectedYear] = useState(
    externalYear !== undefined ? externalYear : new Date().getFullYear()
  );
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    half: 0,
    leave: 0,
    holiday: 0,
    weekend: 0,
    total: 0,
  });

  // Get month name
  const getMonthName = (monthIndex) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthIndex];
  };

  // Get start and end dates of month for API call
  const getMonthDateRange = (month, year) => {
    const startDate = dayjs().year(year).month(month).startOf("month");
    const endDate = dayjs().year(year).month(month).endOf("month");

    return {
      start: formatDateToAPI(startDate),
      end: formatDateToAPI(endDate),
    };
  };

  const handleRowClick = (item) => {
    if (onDayClick) {
      onDayClick({
        date: new Date(item.date || item.attendance_date),
        user: selectedUser,
      });
    }
  };

  const RowCell = ({ item, children }) => (
    <SoftBox
      onClick={() => handleRowClick(item)}
      sx={{
        width: "100%",
        height: "100%",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        // px: 1,
        "&:hover": {
          backgroundColor: "#f9fafb",
        },
      }}
    >
      {children}
    </SoftBox>
  );

  // Fetch monthly attendance data using getAttendenceStatus API
  const fetchMonthlyData = async (month, year) => {
    setLoading(true);
    try {
      // Get date range for the month
      const dateRange = getMonthDateRange(month, year);

      // Prepare params for getAttendenceStatus API
      const params = {
        from: dateRange.start,
        to: dateRange.end,
      };
      if (selectedUser?.id) {
        params.UserId = selectedUser.id;
      }
      const response = await getAttendenceStatus(params);
      if (response?.timeline) {
        processAttendanceData(response.timeline);
      } else {
        setTableData([]);
        setStats({
          present: 0,
          absent: 0,
          half: 0,
          leave: 0,
          holiday: 0,
          weekend: 0,
          total: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching monthly attendance:", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  // Process attendance data from API - handles timeline array structure
  const processAttendanceData = (apiData) => {
    const processedRows = [];
    const newStats = {
      present: 0,
      absent: 0,
      haly: 0,
      leave: 0,
      holiday: 0,
      weekend: 0,
      total: 0,
    };

    let timelineArray = [];

    if (Array.isArray(apiData)) {
      // Direct array response
      timelineArray = apiData;
    } else if (apiData.timeline && Array.isArray(apiData.timeline)) {
      // Object with timeline property
      timelineArray = apiData.timeline;
    } else if (apiData.data && Array.isArray(apiData.data)) {
      // Object with data property
      timelineArray = apiData.data;
    }

    // Sort timeline by date
    timelineArray.sort((a, b) => {
      const dateA = new Date(a.date || a.attendance_date);
      const dateB = new Date(b.date || b.attendance_date);
      return dateB - dateA;
    });

    newStats.total = timelineArray.length;

    timelineArray.forEach((item) => {
      const date = item.date;
      const todayKey = new Date().toISOString().split("T")[0]; // yyyy-mm-dd
      const isToday = item.date === todayKey;
      // Get status from item
      const statusKey = item.status?.toLowerCase() || "";
      const statusReason = item.status_reason?.toLowerCase() || statusKey;

      // Update statistics
      if (statusReason && newStats.hasOwnProperty(statusReason)) {
        newStats[statusReason]++;
      } else if (statusKey && newStats.hasOwnProperty(statusKey)) {
        newStats[statusKey]++;
      }

      // Get check-in/check-out times from API response
      const inTime = item.in || item.first_in || item.check_in;
      const outTime = item.out || item.last_out || item.check_out;
      const workedMinutes = item.worked_minutes || 0;
      const overtimeIn = item?.overtime?.in || 0;
      const overtimeOut = item.overtime?.out || 0;
      const overtimeMinutes = item.overtime?.overtime_worked_minutes || 0;
      const overtimeBreakMinutes =
        item?.overtime?.status === "running"
          ? item?.overtime?.break?.minutes || 0
          : item?.overtime?.overtime_break_minutes || 0;

      const shiftStart = item.shift_start || "09:00";
      const shiftEnd = item.shift_end || "18:00";

      // Get attendance status
      const attendanceStatus = item.attendance_status || item.status;
      const breakMinutes =
        attendanceStatus === "running"
          ? item?.breaks?.minutes || 0
          : item?.attendance_break_minutes || 0;

      processedRows.push({
        date: (
          <RowCell item={item}>
            {isToday ? (
              <SoftTypography variant="button" fontWeight="bold">
                {date}
              </SoftTypography>
            ) : (
              date
            )}
          </RowCell>
        ),

        shiftTime: (
          <RowCell item={item}>
            <ShiftTimeCell shift_start={shiftStart} shift_end={shiftEnd} />
          </RowCell>
        ),

        firstIn: <RowCell item={item}>{formatTime(inTime)}</RowCell>,

        lastOut: <RowCell item={item}>{formatTime(outTime)}</RowCell>,

        totalHours: (
          <RowCell item={item}>
            <SoftBox display="flex" alignItems="center" justifyContent="center">
              {workedMinutes}
            </SoftBox>
          </RowCell>
        ),

        overtimeIn: <RowCell item={item}>{overtimeIn || "-"}</RowCell>,

        overtimeOut: <RowCell item={item}>{overtimeOut || "-"}</RowCell>,

        overtimeMinutes: <RowCell item={item}>{overtimeMinutes || "-"}</RowCell>,

        overtimeBreak: (
          <RowCell item={item}>
            <BreaksCell
              minutes={overtimeBreakMinutes}
              isRunning={item?.overtime?.status === "running"}
            />
          </RowCell>
        ),

        breaks: (
          <RowCell item={item}>
            <BreaksCell minutes={breakMinutes} isRunning={attendanceStatus === "running"} />
          </RowCell>
        ),

        status: (
          <RowCell item={item}>
            <StatusCell
              status={statusKey}
              status_reason={statusReason}
              attendance_status={attendanceStatus}
            />
          </RowCell>
        ),
      });
    });

    setTableData(processedRows);
    setStats(newStats);
  };

  // Handle month change
  const handleMonthChange = (event) => {
    const newMonth = parseInt(event.target.value);
    setSelectedMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  // Handle year change
  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value);
    setSelectedYear(newYear);
    if (onYearChange) {
      onYearChange(newYear);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchMonthlyData(selectedMonth, selectedYear);
  };

  // Fetch data on mount and when month/year changes
  useEffect(() => {
    fetchMonthlyData(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear, selectedUser]);

  // Sync with external props
  useEffect(() => {
    if (externalMonth !== undefined) {
      setSelectedMonth(externalMonth);
    }
    if (externalYear !== undefined) {
      setSelectedYear(externalYear);
    }
  }, [externalMonth, externalYear]);

  // Table columns
  const columns = [
    { Header: "Date", accessor: "date", align: "right" },
    { Header: "Shift Time", accessor: "shiftTime", align: "center" },
    { Header: "Check In", accessor: "firstIn", align: "center" },
    { Header: "Check Out", accessor: "lastOut", align: "center" },
    { Header: "Work Minutes", accessor: "totalHours", align: "center" },
    { Header: "Overtime In", accessor: "overtimeIn", align: "center" },
    { Header: "overtime Out", accessor: "overtimeOut", align: "center" },
    { Header: "overtime Minutes", accessor: "overtimeMinutes", align: "center" },
    { Header: "overtime Break", accessor: "overtimeBreak", align: "center" },

    { Header: "Break", accessor: "breaks", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
  ];

  return (
    <SoftBox>
      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          {/* <CircularProgress /> */}
          <FlagLoader />
          {/* <SoftTypography sx={{ ml: 2 }}>Loading attendance data...</SoftTypography> */}
        </Box>
      )}

      {/* No Data State */}
      {!loading && tableData.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No attendance data available for {getMonthName(selectedMonth)} {selectedYear}
        </Alert>
      )}

      {/* DataTable */}
      {!loading && tableData.length > 0 && (
        <>
          <Grid container spacing={2} mb={2} justifyContent="flex-end" mt={-7.5}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  variant="outlined"
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderRadius: 1.5,
                  }}
                >
                  {[
                    "January",
                    "February",
                    "March",
                    "April",
                    "May",
                    "June",
                    "July",
                    "August",
                    "September",
                    "October",
                    "November",
                    "December",
                  ].map((month, index) => (
                    <MenuItem key={month} value={index} sx={{ fontSize: "0.85rem" }}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedYear}
                  onChange={handleYearChange}
                  variant="outlined"
                  sx={{
                    backgroundColor: "background.paper",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderRadius: 1.5,
                  }}
                >
                  {Array.from({ length: 11 }, (_, i) => {
                    const y = new Date().getFullYear() - 5 + i;
                    return (
                      <MenuItem key={y} value={y} sx={{ fontSize: "0.85rem" }}>
                        {y}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <DataTable
            table={{
              columns,
              rows: tableData,
            }}
            entriesPerPage={{ defaultValue: 31, entries: [31, 61, 91] }}
            showTotalEntries={true}
            isSorted={true}
            noEndBorder={false}
            pagination={{ variant: "gradient", color: "info" }}
          />
        </>
      )}
    </SoftBox>
  );
};

AttendanceTable.propTypes = {
  selectedMonth: PropTypes.number,
  selectedYear: PropTypes.number,
  onMonthChange: PropTypes.func,
  onYearChange: PropTypes.func,
  onDayClick: PropTypes.func,
  selectedUser: PropTypes.object,
};

export default AttendanceTable;
